import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AnyPresentableResource } from '@timelyapp/integration-cloud-api-spec';
import { fetchClient } from '@timelyapp/integration-cloud-sdk/api';
import { getAnalyticsSessionIdAsNumber } from 'tracking/session';
import type { BulkError } from '~api';
import api from '~api';
import * as hoursApi from '~api/hours/hours.api';
import { extractBulkErrorMessage } from '~factories/api';
import { hourDraftFactory, normalizedHourDraftToXhrFactory } from '~factories/hours';
import { getIsDayLockedByDate } from '~redux/modules/dayProperties.selectors';
import { fetchHours } from '~redux/modules/hours.actions';
import type {
  GetTimesheetProposalApiResponse,
  UpdateTimesheetProposalStatusApiArg,
} from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import { getContextualTicAccounts } from '~redux/modules/tic/tic.auth.selectors';
import { showToast } from '~redux/modules/toasts.actions';
import { getCurrentAccountId } from '~redux/selectors/accounts';
import { getProjectVariantMap } from '~redux/selectors/projects';
import { getUserId } from '~redux/selectors/user';
import type { IState } from '~redux/state';
import timelyAnalytics from '~tracking/timely-analytics';
import type { ExternalHourLink, HourDraft, HourTimestamp, NormalizedHourToApi } from '~types/hour';
import type { Project, ProjectVariantMap } from '~types/project';
import { fetchInBatchesOf } from '~utils/api';
import debug from '~utils/debug';
import { calculateDuration } from '~utils/durations';
import { mergeOverlappingTimestampsContainingTicMemories } from '~utils/timestamps';
import { getAnalyticsForTimesheetSubmitted } from '../utils/util.analytics';
import { deleteAutosheetProposalHours, isPartialRevert } from '../utils/util.autosheetHourDeletion';
import type { ClaimSpec } from '../utils/util.entryClaim';
import { executeClaim } from '../utils/util.entryClaim.execute';
import { entryIdsWithItems, partitionItemsByContainment } from '../utils/util.itemContainment';
import { getLastAcceptedHourIds } from '../utils/util.logHelpers';
import type { TimeRange } from '../utils/util.timesheetTransformers';
import {
  setAcceptTimesheetLoading,
  setFlashingEntryIds,
  setFullProjects,
  setIsLoadingSelectedProjects,
  setIsRegeneratingEntry,
  setRegeneratingNotesForDate,
  setRevertTimesheetLoading,
} from './tic.timesheetProposals.slice';

/**
 * Convert a resource to an ExternalHourLink
 */
function resourceToExternalLink(resource: AnyPresentableResource): ExternalHourLink | null {
  if (!resource.tool?.id) {
    return null;
  }

  return {
    provider_type: resource.tool.id,
    provider_id: resource.metadata?.external_code ?? null,
    external_id: resource.external_id,
    uri: resource.uri,
  };
}

/**
 * Transform a timesheet proposal entry to an HourDraft object.
 *
 * Post-TT-4761 the entry's authored `started_at`/`ended_at` is the sole source
 * of truth for both duration and the on-hour timestamp range. Items still pass
 * through to the hour as memory references but do NOT shape the hour's range —
 * a resized or drag-created entry must produce an hour matching exactly what
 * the user sees on the timeline.
 */
export function transformProposalEntryToHourDraft(
  entry: NonNullable<GetTimesheetProposalApiResponse['proposal']['entries']>[0],
  proposalDate: string,
  userId: number,
  projectVariantMap: ProjectVariantMap,
  externalLinks?: ExternalHourLink[],
  entryItems: NonNullable<GetTimesheetProposalApiResponse['proposal']['items']> = []
): HourDraft {
  // Timed entries: hour duration = the authored range. Untimed entries
  // (legacy empties): fall back to the stored duration_minutes — guaranteed
  // non-null by the entries_range_or_duration CHECK on the DB.
  const totalDurationSeconds =
    entry.started_at && entry.ended_at
      ? Math.max(
          0,
          (new Date(entry.ended_at).getTime() - new Date(entry.started_at).getTime()) / 1000
        )
      : (entry.duration_minutes ?? 0) * 60;
  const entryDuration = calculateDuration(totalDurationSeconds);

  // The hour gets ONE timestamp covering the entry's authored range. Item
  // timestamps still merge in for memory-link continuity (downstream code
  // pairs hour-timestamps with TIC memories), but they're scoped inside the
  // entry's window — never shape its edges.
  const entryRangeTimestamp: HourTimestamp[] =
    entry.started_at && entry.ended_at
      ? [{ from: entry.started_at, to: entry.ended_at, entry_ids: [] }]
      : [];

  const itemTimestamps: HourTimestamp[] = entryItems
    .filter(item => item.started_at && item.ended_at)
    .map(item => ({
      from: item.started_at!,
      to: item.ended_at!,
      entry_ids: [],
    }));

  const timestamps = mergeOverlappingTimestampsContainingTicMemories([
    ...entryRangeTimestamp,
    ...itemTimestamps,
  ]);

  // Get project information to determine billable status (important-comment)
  // Only 'full' project variants contain the billable property - 'light' variants do not
  // If project is missing or is a 'light' variant, billable is left unset so that
  // hourDraftFactory's default applies and the backend derives billable from the project.
  const projectId = entry.timely_project_id;
  let isBillable: boolean | undefined;

  if (projectId) {
    const projectVariant = projectVariantMap[projectId];
    if (projectVariant?.type === 'full') {
      isBillable = projectVariant.project.billable;
    }
  }

  return hourDraftFactory(
    {
      day: proposalDate,
      note: entry.note || '',
      project: entry.timely_project_id,
      // Stamp the source proposal onto the hour so the backend can key
      // revert on `delete_by_autosheet_proposal_id` (TT-5117).
      autosheet_proposal_id: entry.timesheet_proposal_id,
      user: { id: userId },
      duration: entryDuration,
      hours: Math.floor(totalDurationSeconds / 3600),
      minutes: Math.floor((totalDurationSeconds % 3600) / 60),
      seconds: totalDurationSeconds % 60,
      label_ids: entry.timely_label_ids || [],
      ...(isBillable !== undefined ? { billable: isBillable } : {}),
      external_links: externalLinks || [],
      timestamps,
    },
    {
      interaction: 'TIC+AI Accept generated timesheet',
    }
  );
}

export const acceptTimesheet = createAsyncThunk<
  void,
  {
    /**
     * YYYY-MM-DD we want to accept predictions for.
     */
    date?: string;
    proposalId: string; // Required proposal ID for RTK Query integration
  },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/acceptTimesheet', async (params, thunkAPI) => {
  const { proposalId, date } = params;
  const state = thunkAPI.getState();

  // Set loading state at the beginning
  thunkAPI.dispatch(setAcceptTimesheetLoading(true));

  try {
    // Get required IDs from state
    const userId = getUserId(state);
    const timelyAccountId = getCurrentAccountId(state);
    const { user: ticUser } = getContextualTicAccounts(state);

    if (!userId || !timelyAccountId || !ticUser?.id) {
      throw new Error('Missing required user or account information');
    }

    debug.template.ai('Starting timesheet acceptance', {
      proposalId,
      date,
      userId,
      timelyAccountId,
      subtopic: 'Accept timesheet proposal',
    });

    // Force fresh fetch — cached data may have stale entries/status that cause
    // 0-entry submissions or missed saves (saved_hours empty in log).
    const proposalResult = await thunkAPI.dispatch(
      ticApi.endpoints.getTimesheetProposal.initiate({ id: proposalId }, { forceRefetch: true })
    );

    if (!proposalResult.data?.proposal) {
      throw new Error('Could not fetch timesheet proposal data');
    }

    const proposal = proposalResult.data.proposal;
    const proposalDate = date || proposal.date;

    // Check if the day is locked before attempting to save hours
    const isDayLocked = getIsDayLockedByDate(thunkAPI.getState(), proposalDate);
    if (isDayLocked) {
      throw new Error('The day is locked. Unlock the day to submit your timesheet.');
    }

    // Submit only writes hours for project-assigned entries; orphan items
    // (proposal.unassigned_items) deliberately stay in the proposal's
    // Unassigned bucket. If there's nothing to submit — e.g., a proposal
    // where every activity is still unassigned — surface that explicitly
    // instead of throwing the misleading "No entries found in timesheet
    // proposal" error.
    if (!proposal.entries || proposal.entries.length === 0) {
      throw new Error(
        'Nothing to submit — assign at least one activity to a project before submitting.'
      );
    }

    const bulkHourDrafts: NormalizedHourToApi[] = [];
    const { workspace } = getContextualTicAccounts(state);

    const allItems = proposal.items ?? [];
    const { byEntryId: itemsByEntryId } = partitionItemsByContainment(allItems, proposal.entries);

    // Fetch resources for entries that have resource_id
    const resourceIds = proposal.entries
      .map(entry => entry.resource_id)
      .filter((id): id is string => !!id);

    const resourceMap = new Map<string, ExternalHourLink>();
    if (resourceIds.length > 0 && workspace?.id) {
      // Fetch resources in parallel using the ts-rest client
      const resourcePromises = resourceIds.map(async resourceId => {
        try {
          const result = await fetchClient.resources.getResource({
            params: { resourceId },
          });

          if (result.status === 200 && result.body) {
            const link = resourceToExternalLink(result.body);
            if (link) {
              resourceMap.set(resourceId, link);
            }
          }
        } catch (error) {
          // Silently fail - if resource can't be fetched, just skip external link
          debug.template.ai('Failed to fetch resource for timesheet entry', {
            resourceId,
            error: error instanceof Error ? error.message : String(error),
            subtopic: 'Resource fetch failure',
          });
        }
      });

      await Promise.allSettled(resourcePromises);
    }

    for (const entry of proposal.entries) {
      // Get external link if resource_id is present
      const externalLinks = entry.resource_id
        ? [resourceMap.get(entry.resource_id)].filter((link): link is ExternalHourLink => !!link)
        : undefined;

      // Transform to hour draft
      const hourDraft = transformProposalEntryToHourDraft(
        entry,
        proposalDate,
        userId,
        getProjectVariantMap(thunkAPI.getState()),
        externalLinks,
        itemsByEntryId.get(entry.id) ?? []
      );
      bulkHourDrafts.push(
        normalizedHourDraftToXhrFactory(hourDraft, {
          view_context: 'Timesheet',
          interaction: 'TA2 Submitted AI Generated Timesheet',
          memory_view: 'AutoSheet',
          memory_experience: 'AutoSheet',
          has_timer: false,
        })
      );
    }
    // Replace any prior AutoSheet hours for this proposal before re-saving, while
    // preserving manual entries. Server-first deletes by proposal id even when the
    // accepted log is incomplete (hardening the historic stale-log 0-delete bug),
    // with the logged-id fallback for pre-stamping proposals. This runs on first
    // accepts too — one round-trip that deletes nothing, accepted deliberately: it
    // also sweeps up stamped hours orphaned by a previous accept that saved hours
    // but failed before logging them.
    const previouslySavedHourIds = getLastAcceptedHourIds(proposal);
    const preDeleteResult = await deleteAutosheetProposalHours({
      proposalId,
      expectedHourIds: previouslySavedHourIds,
      deleteByProposalId: hoursApi.bulkDeleteHoursByAutosheetProposalId,
      bulkDelete: hoursApi.bulkDeleteHours,
    });

    debug.template.ai('Deleted prior autosheet hours before re-save', {
      proposalId,
      deletedCount: preDeleteResult.deletedIds.length,
      expectedCount: previouslySavedHourIds.length,
      usedFallback: preDeleteResult.usedFallback,
      subtopic: 'Accept pre-delete path',
    });

    const response = await hoursApi.bulkSaveHours(bulkHourDrafts);

    // If there were errors, show them, roll back saved hours and don't proceed
    if (response.errors.create.length > 0) {
      // rollback any successfully created hours
      await hoursApi.bulkDeleteHours(response.created_ids);
      //refetch hours
      thunkAPI.dispatch(fetchHours({ since: proposalDate, upto: proposalDate }));
      // Extract specific error message from the API response
      // Note: The actual API returns BulkError[] but BulkUpdateResponse types it as ExtendedError[]
      const errorMessage = extractBulkErrorMessage(
        response.errors.create as unknown as BulkError[]
      );
      throw new Error(errorMessage);
    }

    // Mark proposal as accepted
    const statusUpdateArg: UpdateTimesheetProposalStatusApiArg = {
      id: proposalId,
      body: {
        status: 'accepted',
        log_entry: {
          timestamp: new Date().toISOString(),
          action: 'status_changed',
          details: {
            from_status: proposal.status,
            to_status: 'accepted',
            saved_hours: response.created_ids,
            submitted_via: 'timesheet_proposals',
          },
          user_id: ticUser.id,
        },
      },
    };

    const statusUpdateResult = await thunkAPI.dispatch(
      ticApi.endpoints.updateTimesheetProposalStatus.initiate(statusUpdateArg)
    );

    // Manually update the RTK Query cache so subsequent revertTimesheet calls
    // can read saved_hours from the log entry (stale cache was causing 0 entries deleted)
    const updatedProposal = statusUpdateResult.data?.proposal;
    if (updatedProposal) {
      thunkAPI.dispatch(
        ticApi.util.updateQueryData('getTimesheetProposal', { id: proposalId }, draft => {
          Object.assign(draft.proposal, updatedProposal);
        })
      );
    }

    // Refetch hours to update the calendar view and TIC timeline (hours are shown separately from memories)
    await thunkAPI.dispatch(fetchHours({ since: proposalDate, upto: proposalDate, userId }));

    debug.template.ai('Timesheet acceptance completed successfully', {
      proposalId,
      savedHoursCount: response.created_ids.length,
      subtopic: 'Acceptance complete',
      color: 'green',
    });

    thunkAPI.dispatch(
      showToast({
        title: 'Timesheet submitted successfully',
        message: `${response.created_ids.length} time ${
          response.created_ids.length === 1 ? 'entry' : 'entries'
        } saved to your timesheet.`,
        type: 'success',
        dismissAfter: 5000,
      })
    );

    // Clear loading state on success
    thunkAPI.dispatch(setAcceptTimesheetLoading(false));

    // Analytics: fetch accepted count then track (fire-and-forget, never blocks UX)
    thunkAPI
      .dispatch(
        ticApi.endpoints.getTimesheetProposals.initiate(
          { status: 'accepted', limit: 1 },
          { subscribe: false }
        )
      )
      .then(result => {
        const submissionNumber = result.data?.meta?.totalCount;
        timelyAnalytics.trackTic('TA2 Submitted AI Generated Timesheet', {
          ...getAnalyticsForTimesheetSubmitted(proposal),
          timesheet_id: proposalId,
          submission_number: submissionNumber,
        });
      });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

    debug.template.ai('Timesheet acceptance failed - rolling back saved hours', {
      proposalId,
      error: errorMessage,
      subtopic: 'Acceptance error',
    });

    thunkAPI.dispatch(
      showToast({
        title: 'Failed to submit timesheet',
        message: errorMessage,
        type: 'danger',
        dismissAfter: 8000,
      })
    );

    // Clear loading state on error
    thunkAPI.dispatch(setAcceptTimesheetLoading(false));

    throw error;
  }
});

export const revertTimesheet = createAsyncThunk<
  void,
  {
    /**
     * YYYY-MM-DD date of the timesheet to revert.
     */
    date?: string;
    proposalId: string; // Required proposal ID for RTK Query integration
  },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/revertTimesheet', async (params, thunkAPI) => {
  const { proposalId, date } = params;
  const state = thunkAPI.getState();

  // Set loading state at the beginning
  thunkAPI.dispatch(setRevertTimesheetLoading(true));

  try {
    // Get required IDs from state
    const userId = getUserId(state);
    const timelyAccountId = getCurrentAccountId(state);
    const { user: ticUser } = getContextualTicAccounts(state);

    if (!userId || !timelyAccountId || !ticUser?.id) {
      throw new Error('Missing required user or account information');
    }

    debug.template.ai('Starting timesheet revert', {
      proposalId,
      date,
      userId,
      timelyAccountId,
      subtopic: 'Revert timesheet proposal',
    });

    // Force fresh fetch — cached log may lack saved_hours, causing 0 entries deleted on revert.
    const proposalResult = await thunkAPI.dispatch(
      ticApi.endpoints.getTimesheetProposal.initiate({ id: proposalId }, { forceRefetch: true })
    );

    if (!proposalResult.data?.proposal) {
      throw new Error('Could not fetch timesheet proposal data');
    }

    const proposal = proposalResult.data.proposal;

    // Validate that proposal is in submitted state
    if (proposal.status !== 'accepted') {
      throw new Error('Timesheet is not in submitted state and cannot be reverted');
    }

    // Revert is gated on `status === 'accepted'`, which can only be reached
    // after a successful submit that wrote ≥1 hour, so an empty entries set
    // here is a real anomaly. Surface a clearer message than the legacy
    // "No entries found" so the user can report it accurately.
    if (!proposal.entries || proposal.entries.length === 0) {
      throw new Error('Cannot revert: this proposal has no project-assigned entries to undo.');
    }

    // Hour IDs from the accepted submission log — the legacy fallback payload and
    // the accounting baseline for the partial-revert check below.
    const savedHourIds = getLastAcceptedHourIds(proposal);

    // Delete the proposal's hours server-side in one round-trip (no chunking),
    // falling back to the logged ids for pre-stamping proposals whose rows have a
    // NULL autosheet_proposal_id. A partial result (fewer deleted than logged)
    // means some hours were already gone (e.g. deleted out of band); we surface
    // that via a warning toast below but still flip the proposal to 'pending' so
    // it doesn't get stuck in 'submitted' with the data already partly removed.
    const { deletedIds, usedFallback } = await deleteAutosheetProposalHours({
      proposalId,
      expectedHourIds: savedHourIds,
      deleteByProposalId: hoursApi.bulkDeleteHoursByAutosheetProposalId,
      bulkDelete: hoursApi.bulkDeleteHours,
    });
    const partialRevert = isPartialRevert(deletedIds.length, savedHourIds.length);

    debug.template.ai('Reverted autosheet hours', {
      proposalId,
      deletedCount: deletedIds.length,
      expectedCount: savedHourIds.length,
      usedFallback,
      subtopic: 'Revert delete path',
    });

    // Mark proposal as pending (revert from accepted)
    const statusUpdateArg: UpdateTimesheetProposalStatusApiArg = {
      id: proposalId,
      body: {
        status: 'pending',
        log_entry: {
          timestamp: new Date().toISOString(),
          action: 'status_changed',
          details: {
            from_status: proposal.status,
            to_status: 'pending',
            reverted_hours: deletedIds,
            reverted_via: 'timesheet_proposals',
          },
          user_id: ticUser.id,
        },
      },
    };

    const revertStatusResult = await thunkAPI.dispatch(
      ticApi.endpoints.updateTimesheetProposalStatus.initiate(statusUpdateArg)
    );

    // Manually update the RTK Query cache so the UI and any subsequent operations
    // see the reverted proposal state (status: 'pending', reverted_hours logged)
    const revertedProposal = revertStatusResult.data?.proposal;
    if (revertedProposal) {
      thunkAPI.dispatch(
        ticApi.util.updateQueryData('getTimesheetProposal', { id: proposalId }, draft => {
          Object.assign(draft.proposal, revertedProposal);
        })
      );
    }

    // Refetch hours to update the UI immediately (await + userId for proper cache update)
    await thunkAPI.dispatch(fetchHours({ since: proposal.date, upto: proposal.date, userId }));

    const deletedCount = deletedIds.length;
    const entriesWord = deletedCount === 1 ? 'entry' : 'entries';
    thunkAPI.dispatch(
      showToast(
        partialRevert
          ? {
              title: 'Timesheet reverted with fewer entries than expected',
              message:
                `${deletedCount} of ${savedHourIds.length} time ${entriesWord} removed and ` +
                'returned to draft state. The remaining entries were already missing.',
              type: 'warning',
              dismissAfter: 8000,
            }
          : {
              title: 'Timesheet reverted successfully',
              message: `${deletedCount} time ${entriesWord} removed from your timesheet and returned to draft state.`,
              type: 'success',
              dismissAfter: 5000,
            }
      )
    );

    // Clear loading state on success
    thunkAPI.dispatch(setRevertTimesheetLoading(false));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

    debug.template.ai('Timesheet revert failed', {
      proposalId,
      error: errorMessage,
      subtopic: 'Revert error',
    });

    thunkAPI.dispatch(
      showToast({
        title: 'Failed to revert timesheet',
        message: errorMessage,
        type: 'danger',
        dismissAfter: 8000,
      })
    );

    // Clear loading state on error
    thunkAPI.dispatch(setRevertTimesheetLoading(false));

    throw error;
  }
});

export const deleteTimesheet = createAsyncThunk<
  void,
  {
    proposalId: string;
  },
  { dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/deleteTimesheet', async (params, thunkAPI) => {
  const { proposalId } = params;

  const proposalResult = await thunkAPI.dispatch(
    ticApi.endpoints.getTimesheetProposal.initiate({ id: proposalId })
  );

  if (!proposalResult.data?.proposal) {
    throw new Error('Could not fetch timesheet proposal data');
  }

  debug.template.ai('Starting timesheet deletion', {
    proposalId,
    subtopic: 'Delete timesheet proposal',
  });

  thunkAPI
    .dispatch(ticApi.endpoints.deleteTimesheetProposal.initiate({ id: proposalId }))
    .then(() => {
      const successMessage = `Timesheet proposal ${proposalId} with ${proposalResult.data?.proposal?.entries?.length} entries deleted successfully`;
      debug.template.ai('Timesheet deleted successfully', {
        proposalId,
        subtopic: 'Delete success',
      });

      //invalidate the proposal query cache

      thunkAPI.dispatch(
        showToast({
          title: 'Timesheet deleted successfully',
          message: successMessage,
          type: 'success',
        })
      );
    })
    .catch(error => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      debug.template.ai('Timesheet deletion failed', {
        proposalId,
        error: errorMessage,
        subtopic: 'Delete error',
      });
    });
});

const inFlightProjectIds = new Set<number>();

export const fetchFullProjects = createAsyncThunk<
  void,
  number[],
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/fetchFullProjects', async (projectIds, thunkAPI) => {
  const idsToFetch = projectIds.filter(id => !inFlightProjectIds.has(id));
  if (idsToFetch.length === 0) return;

  idsToFetch.forEach(id => inFlightProjectIds.add(id));

  // Set loading state at the beginning
  thunkAPI.dispatch(setIsLoadingSelectedProjects(true));

  try {
    debug.template.ai('Fetching missing project data for timesheet proposal', {
      projectIds: idsToFetch,
      count: idsToFetch.length,
      subtopic: 'Fetch missing projects',
    });

    const projects: Project[] = await fetchInBatchesOf(50, idsToFetch, batch =>
      api.getProjectIds(batch, { includeTotals: false })
    );

    if (projects.length > 0) {
      thunkAPI.dispatch(setFullProjects(projects));
    }

    debug.template.ai('Missing projects fetched successfully', {
      fetchedCount: projects.length,
      requestedCount: idsToFetch.length,
      subtopic: 'Fetch success',
      color: 'green',
    });

    // Clear loading state on success
    thunkAPI.dispatch(setIsLoadingSelectedProjects(false));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

    debug.template.ai('Failed to fetch missing projects', {
      projectIds: idsToFetch,
      error: errorMessage,
      subtopic: 'Fetch error',
    });

    // Clear loading state on error
    thunkAPI.dispatch(setIsLoadingSelectedProjects(false));

    throw error;
  } finally {
    idsToFetch.forEach(id => inFlightProjectIds.delete(id));
  }
});

export const regenerateEntryNotes = createAsyncThunk<
  void,
  {
    date: string;
    proposalId: string;
    entryId?: string; // Optional: regenerate only this specific entry
  },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/regenerateEntryNotes', async (params, thunkAPI) => {
  const { date, proposalId, entryId } = params;
  const state = thunkAPI.getState();

  try {
    const timelyAccountId = getCurrentAccountId(state);
    const timelyUserId = getUserId(state);

    if (!timelyAccountId || !timelyUserId) {
      throw new Error('Missing required account or user information');
    }

    // Set appropriate loading state based on single vs bulk regeneration
    if (entryId) {
      thunkAPI.dispatch(setIsRegeneratingEntry({ entryId, isRegenerating: true }));
    } else {
      thunkAPI.dispatch(setRegeneratingNotesForDate({ date, isRegenerating: true }));
    }

    debug.template.ai('Starting note regeneration', {
      proposalId,
      date,
      entryId,
      subtopic: entryId ? 'Regenerate single entry note' : 'Regenerate entry notes',
    });

    timelyAnalytics.trackTic('TA2 Requested Note Regeneration', {
      timesheet_id: proposalId,
      timesheet_date: date,
      entry_id: entryId,
      regeneration_type: entryId ? 'single' : 'bulk',
    });

    await thunkAPI.dispatch(
      ticApi.endpoints.regenerateEntryNotes.initiate({
        body: {
          date,
          timely_account_id: timelyAccountId,
          timely_user_id: timelyUserId,
          entry_id: entryId,
          session_id: getAnalyticsSessionIdAsNumber(),
        },
      })
    );

    debug.template.ai('Note regeneration triggered successfully', {
      proposalId,
      date,
      entryId,
      subtopic: 'Regeneration started',
      color: 'green',
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

    debug.template.ai('Note regeneration failed', {
      proposalId,
      date,
      entryId,
      error: errorMessage,
      subtopic: 'Regeneration error',
    });

    // Clear appropriate loading state
    if (entryId) {
      thunkAPI.dispatch(setIsRegeneratingEntry({ entryId, isRegenerating: false }));
    } else {
      thunkAPI.dispatch(setRegeneratingNotesForDate({ date, isRegenerating: false }));
    }

    thunkAPI.dispatch(
      showToast({
        title: 'Failed to regenerate note',
        message: errorMessage,
        type: 'danger',
        dismissAfter: 8000,
      })
    );

    throw error;
  }
});

/**
 * Claim one or more time ranges for a project. Used by:
 * - the timeline drag-to-claim gesture (single range, non-null project), and
 * - the reassign-selection UI in ReassignmentControls (one range per contiguous
 *   group of selected items, project optional — null = release claim).
 * Goes through the claim primitive so adjacent same-identity ranges merge
 * transparently.
 */
export const claimRangesForProject = createAsyncThunk<
  { targetEntryIds: string[]; affectedEntryIds: string[] },
  {
    proposalId: string;
    ranges: TimeRange[];
    projectId: number | null;
    labelIds: number[];
    resourceId: string | null;
  },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/claimRangesForProject', async (params, thunkAPI) => {
  const { proposalId, ranges, projectId, labelIds, resourceId } = params;

  if (ranges.length === 0) {
    return { targetEntryIds: [], affectedEntryIds: [] };
  }

  const state = thunkAPI.getState();
  const cachedQuery = ticApi.endpoints.getTimesheetProposal.select({ id: proposalId })(state);
  let proposal = cachedQuery.data?.proposal;
  if (!proposal) {
    const proposalResult = await thunkAPI.dispatch(
      ticApi.endpoints.getTimesheetProposal.initiate({ id: proposalId })
    );
    proposal = proposalResult.data?.proposal;
  }

  if (!proposal) {
    thunkAPI.dispatch(
      showToast({
        message: 'Unable to apply changes — no timesheet proposal available',
        type: 'danger',
      })
    );
    throw new Error('Could not fetch timesheet proposal data');
  }

  // Date for newly-created entries is the proposal's own day, not the caller's
  // local calendar.
  const date = proposal.date;

  try {
    const baseClaim: Omit<ClaimSpec, 'range'> = {
      projectId,
      labelIds: [...labelIds].sort((a, b) => a - b),
      resourceId,
      date,
      note: null,
    };
    const allTargetEntryIds = new Set<string>();
    const allAffectedEntryIds = new Set<string>();
    // Entries the claim created from scratch. These carry the claimed time but
    // no note — the source entry they were carved out of is deleted/split away.
    // We regenerate a note for them below so reassigning doesn't lose context.
    const createdTargetEntryIds = new Set<string>();

    let entries = proposal.entries ?? [];

    for (const range of ranges) {
      const result = await executeClaim({
        claim: { ...baseClaim, range },
        entries,
        proposalId: proposal.id,
        dispatch: thunkAPI.dispatch,
      });

      if (result.targetEntryId) {
        allTargetEntryIds.add(result.targetEntryId);
        if (result.targetEntryCreated) createdTargetEntryIds.add(result.targetEntryId);
      }
      result.affectedEntryIds.forEach(id => allAffectedEntryIds.add(id));

      // See executeClaim's docstring: callers running multi-range must refresh
      // entries between iterations. Different-identity entries may span
      // gap-separated ranges (e.g. one big unassigned block touched by two
      // selections), so each iteration needs the post-effect geometry from the
      // optimistic cache.
      const refreshedQuery = ticApi.endpoints.getTimesheetProposal.select({
        id: proposalId,
      })(thunkAPI.getState());
      entries = refreshedQuery.data?.proposal?.entries ?? entries;
    }

    // Synthetic "unassigned" key for flash UX when target was a release.
    if (projectId === null) allTargetEntryIds.add('unassigned');

    thunkAPI.dispatch(
      setFlashingEntryIds(Array.from(new Set([...allTargetEntryIds, ...allAffectedEntryIds])))
    );
    // Invalidate the proposal-list cache (so list views re-fetch summaries),
    // but NOT the full proposal cache — each effect's optimistic update has
    // already brought it to the post-claim state, and refetching there causes
    // back-to-back re-renders that interrupt the flash animation.
    thunkAPI.dispatch(ticApi.util.invalidateTags(['TimesheetProposals']));

    // Assigning unassigned time to a project replaces the source entry with a
    // fresh one, which has no note. Regenerate notes for just those new entries
    // so users keep the context they had. Release claims (projectId === null)
    // produce no created entries, so this no-ops for "move to unassigned".
    // Skip entries with no contained activities (e.g. drag-claiming empty
    // timeline space)
    // Fire-and-forget: a regen failure must not fail the claim that already
    // succeeded — regenerateEntryNotes surfaces its own error toast.
    for (const entryId of entryIdsWithItems(createdTargetEntryIds, proposal.items ?? [], entries)) {
      void thunkAPI.dispatch(regenerateEntryNotes({ date, proposalId, entryId }));
    }

    return {
      targetEntryIds: Array.from(allTargetEntryIds),
      affectedEntryIds: Array.from(allAffectedEntryIds),
    };
  } catch (error) {
    console.error('Failed to apply claim:', error);
    thunkAPI.dispatch(ticApi.util.invalidateTags(['TimesheetProposal', 'TimesheetProposals']));
    thunkAPI.dispatch(showToast({ message: 'Failed to apply changes', type: 'danger' }));
    throw error;
  }
});
